import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '@/store';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Store, ShoppingCart, Package, Wallet, TrendingUp,
  Clock, AlertTriangle, Truck, CheckCircle, XCircle,
  ArrowRight, CreditCard, Archive, Eye,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (val) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val || 0);

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '-'; }
};

export default function AdminDashboardPage() {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/b2b-dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-3 sm:space-y-6" data-testid="admin-dashboard-page">
        <div><h1 className="text-xl sm:text-2xl font-bold">B2Bonix - Yönetim Paneli</h1></div>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          {[1,2,3,4].map(i => <Card key={i}><CardContent className="p-5"><Skeleton className="h-16 w-full" /></CardContent></Card>)}
        </div>
      </div>
    );
  }

  const s = data || {};

  const mainStats = [
    { label: 'Toplam Bayi', value: s.total_dealers || 0, sub: `${s.active_dealers || 0} aktif`, icon: Store, link: '/admin/dealers' },
    { label: 'Toplam Sipariş', value: s.total_orders || 0, sub: `${s.monthly_orders || 0} bu ay`, icon: ShoppingCart, link: '/admin/orders' },
    { label: 'Toplam Ciro', value: formatCurrency(s.total_revenue), sub: `Bu ay: ${formatCurrency(s.monthly_revenue)}`, icon: TrendingUp, link: '/admin/orders' },
    { label: 'Bekleyen Ödeme', value: s.pending_payments || 0, sub: `${s.overdue_promises || 0} vadesi geçmiş`, icon: Wallet, link: '/admin/payment-tracking' },
  ];

  const orderStats = [
    { label: 'Beklemede', value: s.pending_orders || 0, icon: Clock, filter: 'pending' },
    { label: 'Hazırlanıyor', value: s.preparing_orders || 0, icon: Package, filter: 'preparing' },
    { label: 'Sevkiyatta', value: s.shipped_orders || 0, icon: Truck, filter: 'shipped' },
    { label: 'Tamamlandı', value: s.completed_orders || 0, icon: CheckCircle, filter: 'completed' },
    { label: 'İptal', value: s.cancelled_orders || 0, icon: XCircle, filter: 'cancelled' },
  ];

  const getStatusLabel = (status) => {
    const map = { pending: 'Beklemede', preparing: 'Hazırlanıyor', shipped: 'Kargoda', in_transit: 'Sevkiyatta', completed: 'Tamamlandı', cancelled: 'İptal' };
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    if (status === 'cancelled') return 'bg-red-50 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getPaymentLabel = (ps) => {
    if (ps === 'paid') return 'Ödendi';
    if (ps === 'on_account') return 'Cari Borç';
    return 'Bekliyor';
  };

  const getPaymentColor = (ps) => {
    if (ps === 'paid') return 'bg-gray-100 text-gray-700';
    if (ps === 'on_account') return 'bg-gray-100 text-gray-600';
    return 'bg-red-50 text-red-700';
  };

  return (
    <div className="space-y-3 sm:space-y-5" data-testid="admin-dashboard-page">
      {/* Baslik */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">B2Bonix - Yönetim Paneli</h1>
        <p className="text-sm text-gray-500 mt-1">B2B Bayi Portali genel bakış</p>
      </div>

      {/* Ana Istatistikler */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {mainStats.map((stat, i) => (
          <Link key={i} to={stat.link} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 hover:shadow-md hover:border-gray-200 transition-all cursor-pointer" data-testid={`main-stat-${i}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-500 truncate">{stat.label}</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 mt-0.5">{stat.value}</p>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 truncate">{stat.sub}</p>
              </div>
              <div className={`rounded-lg p-2 sm:p-2.5 bg-gray-100 text-gray-500 shrink-0 ml-2`}>
                <stat.icon className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Sipariş Durumları + Ödeme Takibi (alt alta) */}
      <div className="flex flex-col gap-4">
        {/* Sipariş Durumları */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Sipariş Durumları</h2>
            <Link to="/admin/orders" className="text-xs text-[#1a224f] hover:text-[#12173a] flex items-center gap-1">Tümü <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="p-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
            {orderStats.map((os, i) => (
              <Link key={i} to={`/admin/orders?status=${os.filter}`} className="flex flex-col items-center justify-center px-2 py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 hover:border-gray-300 border border-transparent transition-all text-center" data-testid={`order-stat-${i}`}>
                <div className="p-1.5 rounded-md bg-white text-gray-500 shadow-sm mb-1">
                  <os.icon className="w-4 h-4" />
                </div>
                <p className="text-lg font-bold text-gray-900 leading-none">{os.value}</p>
                <p className="text-[10px] text-gray-500 truncate mt-0.5 w-full">{os.label}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Ödeme Takibi — 4 stat yan yana */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Ödeme Takibi</h2>
            <Link to="/admin/payment-tracking" className="text-xs text-[#1a224f] hover:text-[#12173a] flex items-center gap-1">Tümü <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Link to="/admin/payment-tracking?status=pending" className="flex flex-col px-3 py-3 rounded-lg bg-amber-50/60 hover:bg-amber-50 border border-amber-100 hover:border-amber-200 transition-all" data-testid="payment-stat-pending">
              <div className="flex items-center justify-between">
                <Wallet className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-base font-bold text-amber-700 tabular-nums">{s.pending_payments || 0}</span>
              </div>
              <p className="text-[11px] text-gray-600 mt-1">Bekleyen Toplam</p>
              <p className="text-[11px] font-semibold text-amber-700 tabular-nums truncate">{formatCurrency(s.total_pending_amount || 0)}</p>
            </Link>

            <Link to="/admin/payment-tracking?status=overdue" className="flex flex-col px-3 py-3 rounded-lg bg-red-50/60 hover:bg-[#eef0f7] border border-red-100 hover:border-red-200 transition-all" data-testid="payment-stat-overdue">
              <div className="flex items-center justify-between">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                <span className="text-base font-bold text-red-700 tabular-nums">{s.overdue_promises || 0}</span>
              </div>
              <p className="text-[11px] text-gray-600 mt-1">Vadesi Geçen</p>
              <p className="text-[11px] font-semibold text-red-700 tabular-nums truncate">{formatCurrency(s.overdue_amount || 0)}</p>
            </Link>

            <Link to="/admin/payment-tracking?status=due_today" className="flex flex-col px-3 py-3 rounded-lg bg-blue-50/60 hover:bg-blue-50 border border-blue-100 hover:border-blue-200 transition-all" data-testid="payment-stat-today">
              <div className="flex items-center justify-between">
                <Clock className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="text-base font-bold text-blue-700 tabular-nums">{s.due_today || 0}</span>
              </div>
              <p className="text-[11px] text-gray-600 mt-1">Bugün Vadeli</p>
              <p className="text-[11px] font-semibold text-blue-700 tabular-nums truncate">&nbsp;</p>
            </Link>

            <Link to="/admin/payment-tracking?status=paid" className="flex flex-col px-3 py-3 rounded-lg bg-green-50/60 hover:bg-green-50 border border-green-100 hover:border-green-200 transition-all" data-testid="payment-stat-paid">
              <div className="flex items-center justify-between">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                <span className="text-base font-bold text-green-700 tabular-nums">{s.paid_this_month || 0}</span>
              </div>
              <p className="text-[11px] text-gray-600 mt-1">Tahsil Edilen</p>
              <p className="text-[11px] font-semibold text-green-700 tabular-nums truncate">{formatCurrency(s.paid_this_month_amount || 0)}</p>
            </Link>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Hızlı Bilgiler — Bayiler stili */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5">
            <h2 className="text-sm font-semibold text-gray-900">Hızlı Bilgiler</h2>
          </div>
          <table className="w-full text-[13px]">
            <tbody>
              {[
                { label: 'Toplam Ürün', value: s.total_products || 0, icon: Archive, link: '/admin/products' },
                { label: 'Düşük Stoklu Ürünler', value: s.low_stock_products || 0, icon: AlertTriangle, link: '/admin/products', warn: true },
                { label: 'Ödenmiş Siparişler', value: s.paid_orders || 0, icon: CreditCard, link: '/admin/orders' },
                { label: 'Ödeme Sözleri', value: s.payment_promises || 0, icon: Wallet, link: '/admin/payment-tracking' },
              ].map((item, i) => (
                <tr key={i} className={`border-b border-gray-100 last:border-0 ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50 cursor-pointer`} onClick={() => navigate(item.link)}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <item.icon className={`w-4 h-4 ${item.warn ? 'text-red-500' : 'text-gray-400'}`} />
                      <span className="text-gray-700">{item.label}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <span className={`text-sm font-bold ${item.warn && item.value > 0 ? 'text-[#1a224f]' : 'text-gray-900'}`}>{item.value}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* En Çok Sipariş Verenler — Bayiler stili */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">En Çok Sipariş Verenler</h2>
            <Link to="/admin/dealers" className="text-xs text-[#1a224f] hover:text-[#12173a] flex items-center gap-1">Tümü <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {(s.top_dealers || []).length > 0 ? (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="w-10 px-2 py-2 text-center text-xs font-semibold text-gray-500">#</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Bayi</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-24">Sipariş</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {s.top_dealers.map((td, i) => (
                  <tr key={i} className={`border-b border-gray-100 last:border-0 ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50`}>
                    <td className="px-2 py-2.5 text-center text-gray-400 text-xs">{i + 1}</td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-gray-900 truncate">{td.company || td.name}</p>
                      {td.company && td.name && td.company !== td.name && <p className="text-[11px] text-gray-400 truncate">{td.name}</p>}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-600">{td.order_count}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-gray-900 tabular-nums">{formatCurrency(td.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Henüz sipariş verisi yok</p>
          )}
        </div>
      </div>

      {/* Son Siparişler — Bayiler stili */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Son Siparişler</h2>
          <Link to="/admin/orders" className="text-xs text-[#1a224f] hover:text-[#12173a] flex items-center gap-1">Tümü <ArrowRight className="w-3 h-3" /></Link>
        </div>
        {(s.recent_orders || []).length > 0 ? (
          <>
            {/* Desktop Tablo */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="w-10 px-2 py-2 text-center text-xs font-semibold text-gray-500">#</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Sipariş No</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Tarih</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Tutar</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-32">Ödeme</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-32">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {s.recent_orders.slice(0, 8).map((order, i) => (
                    <tr key={order.id} className={`border-b border-gray-100 last:border-0 cursor-pointer ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50`} onClick={() => navigate(`/admin/orders?detail=${order.id}`)}>
                      <td className="px-2 py-2 text-center text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">#{order.order_number}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{formatDate(order.created_at)}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatCurrency(order.total_amount)}</td>
                      <td className="px-3 py-2 text-center"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getPaymentColor(order.payment_status)}`}>{getPaymentLabel(order.payment_status)}</span></td>
                      <td className="px-3 py-2 text-center"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(order.status)}`}>{getStatusLabel(order.status)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobil Kartlar */}
            <div className="md:hidden divide-y divide-gray-100">
              {s.recent_orders.slice(0, 6).map((order) => (
                <div key={order.id} className="p-3 flex items-center justify-between gap-3 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/admin/orders?detail=${order.id}`)}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">#{order.order_number}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${getStatusColor(order.status)}`}>{getStatusLabel(order.status)}</span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">{formatDate(order.created_at)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-gray-900">{formatCurrency(order.total_amount)}</p>
                    <span className="text-[10px] font-medium text-gray-500">{getPaymentLabel(order.payment_status)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-gray-400">
            <ShoppingCart className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm">Henüz sipariş yok</p>
          </div>
        )}
      </div>
    </div>
  );
}
